// DefendantProfileDialog.jsx
import React, {useEffect, useState} from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Paper,
    TextField,
    Typography
} from '@mui/material';
import {useClient} from '../core/client';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import CheckinSchedule from '../checkin/CheckinSchedule';
import StateSelector from "../core/components/StateSelector";

const DefendantProfileDialog = ({open, onClose, id}) => {
    const [defendant, setDefendant] = useState(null);
    const [formData, setFormData] = useState({
        first: '',
        last: '',
        dob: '',
        phone: '',
        email: '',
        address: '',
        city: '',
        state: '',
        zip: '',
    });
    const [loading, setLoading] = useState(true);
    const [errors, setErrors] = useState({}); // Error state
    const client = useClient();

    useEffect(() => {
        const fetchDefendant = async () => {
            try {
                const response = await client.get(`/defendants/${id}`);
                setDefendant(response.data);
            } catch (error) {
                console.error('Error fetching defendant:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchDefendant();
    }, [client, id]);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
        setErrors({}); // Clear error when user starts typing
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            {loading ? (
                <DialogTitle>Defendant Profile</DialogTitle>
            ) : (
                <DialogTitle>
                    {defendant.first} {defendant.last}
                </DialogTitle>
            )}
            <DialogContent>
                {loading ? (
                    <CircularProgress/>
                ) : !defendant ? (
                    <Typography variant="h6">Defendant not found</Typography>
                ) : (
                    <Paper sx={{p: 3, maxWidth: 600, margin: '0 auto'}}>
                        <Typography variant="h5" gutterBottom>
                            Defendant Info
                        </Typography>
                        <Box component="form" noValidate sx={{mt: 2}}>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        name="first"
                                        label="First Name"
                                        value={defendant.first}
                                        onChange={handleChange}
                                        required
                                        error={!!errors.first}
                                        helperText={errors.first}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        name="last"
                                        label="Last Name"
                                        value={defendant.last}
                                        onChange={handleChange}
                                        required
                                        error={!!errors.last}
                                        helperText={errors.last}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        name="dob"
                                        label="Date of Birth"
                                        type="date"
                                        value={defendant.dob}
                                        onChange={handleChange}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        required
                                        error={!!errors.dob}
                                        helperText={errors.dob}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        name="phone"
                                        label="Phone Number"
                                        value={defendant.phone}
                                        onChange={handleChange}
                                        required
                                        error={!!errors.phone}
                                        helperText={errors.phone}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        name="email"
                                        label="Email Address"
                                        type="email"
                                        value={defendant.email}
                                        onChange={handleChange}
                                        required
                                        error={!!errors.email}
                                        helperText={errors.email}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        name="address"
                                        label="Address"
                                        value={defendant.address}
                                        onChange={handleChange}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField
                                        fullWidth
                                        name="city"
                                        label="City"
                                        value={defendant.city}
                                        required={!!formData.address}
                                        onChange={handleChange}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <StateSelector
                                        name="state"
                                        value={defendant.state}
                                        onChange={handleChange}
                                        error={!!errors.state}
                                        required={!!formData.address}
                                        helperText={errors.state}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField
                                        fullWidth
                                        name="zip"
                                        label="ZIP Code"
                                        value={defendant.zip}
                                        onChange={handleChange}
                                        required={!!formData.address}
                                        error={!!errors.zip}
                                        helperText={errors.zip}
                                    />
                                </Grid>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <CheckinSchedule defendantId={id} checkinSchedule={defendant.checkinSchedules} />
                                </LocalizationProvider>
                            </Grid>
                        </Box>
                    </Paper>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="secondary">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DefendantProfileDialog;
